import {
	hideLoadingScreen,
	router,
	setPageContent,
	showLoadingScreen,
} from "..";
import { delFencealarm2, putFencealarm2 } from "../api/api";
import { getPrices } from "../api/prices";
import {
	nameField,
	ownerField,
	sharingField,
	subscriptionField,
	tagsField,
} from "../device/device_settings_fields";
import { openUnregisterDeviceModal } from "../device/unregister_device_modal";
import { locale } from "../locale";
import { state } from "../state";
import { Bulma } from "../view/bulma";
import { centeredBox, errorToast } from "../view/components";

/**
 * @type {import('navigo').Handler}
 */
async function fencealarm2SettingsPage(ctx) {
	if (!ctx || !ctx.data || !ctx.data.imei) throw "missing ctx.data.imei";
	const fa2 = state.fencealarm2s[ctx.data.imei];

	const user = await state.getActiveUser();
	const currency = user.Country === "SE" ? "SEK" : "EUR";
	const isOwner = user.Email === fa2.Metadata.OwnerEmail;

	const { Fencealarm2Month, Fencealarm2Year } = await getPrices();

	const voltageAlarm = voltageAlarmField(fa2);
	const subscription = subscriptionField(
		fa2.Subscription,
		[Fencealarm2Month, Fencealarm2Year],
		currency,
		isOwner,
	);
	const sharing = sharingField(fa2.Metadata);
	const tags = tagsField(fa2.Metadata);
	const name = nameField(fa2.Metadata.Name);
	const owner = ownerField(fa2.Metadata, isOwner);

	const save = () => {
		showLoadingScreen();
		putFencealarm2(fa2.Imei, {
			VoltageAlarm: {
				Enabled: voltageAlarm.isEnabled(),
				Threshold: +voltageAlarm.getThreshold(),
			},
			Metadata: {
				Access: sharing.getAccess(),
				Notify: sharing.getNotify(),
				Tags: tags.getValue(),
				Name: name.getValue(),
				Owner: owner.getValue(),
			},
			Subscription: subscription.getPriceId(),
		})
			.then((fa2) => {
				state.fencealarm2s[fa2.Imei] = fa2;
				router.jump(`/devices?id=${fa2.Imei}`);
			})
			.catch(errorToast)
			.finally(hideLoadingScreen);
	};

	const unregister = () => {
		showLoadingScreen();
		delFencealarm2(fa2.Imei)
			.then(() => {
				delete state.fencealarm2s[fa2.Imei];
				router.jump("/devices");
			})
			.catch(errorToast)
			.finally(hideLoadingScreen);
	};

	setPageContent(
		centeredBox(
			"small",
			Bulma.title(`FenceAlarm2 - ${fa2.Metadata.Name}`, "is-4"),
			Bulma.subtitle(`IMEI: ${fa2.Imei}`, "is-6"),
			voltageAlarm.element,
			subscription.element,
			sharing.element,
			tags.element,
			name.element,
			owner.element,
			document.createElement("hr"),
			Bulma.buttons(
				Bulma.button(
					{
						onClick: save,
						styles: ["is-primary"],
					},
					locale.strings.save,
				),
				Bulma.button(
					{
						onClick: () => router.jump(`/devices?id=${fa2.Imei}`),
						styles: ["is-primary"],
					},
					locale.strings.back,
				),
				Bulma.button(
					{
						disabled: !isOwner,
						onClick: () =>
							openUnregisterDeviceModal(
								"FenceAlarm2",
								unregister,
							),
						styles: ["is-danger"],
					},
					locale.strings.unregister,
				),
			),
		),
	);
}

/**
 * @param {Fencealarm2} fa2
 */
function voltageAlarmField(fa2) {
	const isSubscribed =
		fa2.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(fa2.Subscription.ExpiresAt).getTime();

	const onOffSelect = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(
				locale.strings.on,
				"on",
				isSubscribed && fa2.VoltageAlarm.Enabled,
			),
			Bulma.option(
				locale.strings.off,
				"off",
				!isSubscribed || !fa2.VoltageAlarm.Enabled,
			),
		],
	});

	let selectedAlarmLevel = fa2.Config_v0_3.AlarmLevel;
	if (fa2.PendingResponse_v0_3 && fa2.PendingResponse_v0_3.Config) {
		selectedAlarmLevel = fa2.PendingResponse_v0_3.Config.AlarmLevel;
	}

	const alarmLevelSelect = Bulma.select({
		options: Array.from({ length: 7 }, (_, i) => (i + 2) * 1000).map((i) =>
			Bulma.option(`${i} V`, i.toString(), i === selectedAlarmLevel),
		),
	});

	const element = Bulma.field(
		{},
		Bulma.label(
			locale.string({
				enGB: "Voltage alarm",
				deDE: "Spannungsalarm",
				frFR: "Alarme de tension",
				svSE: "Spänningslarm",
			}),
		),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOffSelect.element),
			Bulma.control({}, alarmLevelSelect.element),
		),
	);

	return {
		element,
		isEnabled: () => onOffSelect.getValue() === "on",
		getThreshold: () => alarmLevelSelect.getValue(),
	};
}

export { fencealarm2SettingsPage };
